import React from 'react';
import { CheckIcon, StarIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { TrustLine } from './TrustLine';
type Volume = {
  number: string;
  title: string;
  phase: string;
  headline: string;
  body: string;
  promise: string;
  heart?: boolean;
};
const volumes: Volume[] = [{
  number: '01',
  title: 'The Foundation',
  phase: '12–9 months out',
  headline: 'Money, People, Place — in that order',
  body: 'The REAL budget, including every line couples forget: alterations, postage, vendor meals, tips, and the service fee sitting 15–25% on top of the catering bill. Plus The Guest-List Math, the date-and-venue filter, and The Vendor Booking Order — which vendor gets booked first, and why booking out of order costs money.',
  promise: 'You’ll know where the money goes before it goes.'
}, {
  number: '02',
  title: 'The Vendor Playbook',
  phase: '9–6 months out',
  headline: 'What to ask, what’s included, what’s a red flag',
  body: 'One Expectation Sheet per vendor: the exact questions to ask a photographer, a caterer, a DJ, a florist. The Quote Comparison Grid that makes three differently-formatted quotes finally comparable. What’s usually included, what’s always extra, and the follow-up scripts for when a vendor goes quiet.',
  promise: 'You walk into every vendor call knowing more than you did last time.'
}, {
  number: '03',
  title: 'The Details',
  phase: '6–3 months out',
  headline: 'Dress, stationery, menu, music',
  body: 'Fitting timelines that actually work backwards from the date. Stationery and invitation deadlines (with the postage math). Menu, bar and tasting decisions. Music and the moments that need it.',
  promise: 'Every decision at the month it’s actually due — not three months early, and never too late.'
}, {
  number: '04',
  title: 'The Countdown',
  phase: '3–1 months out',
  headline: 'RSVPs, seating, and who does what',
  body: 'RSVP chasing scripts, the seating chart method that doesn’t take a weekend, the hour-by-hour timeline of the day, and the who-does-what assignment sheet that stops the work from landing on one person.',
  promise: 'The last three months stop being a mystery and start being a schedule.'
}, {
  number: '05',
  title: 'The Final Month',
  phase: 'The two weeks nobody warns you about',
  headline: 'Day by day through the final week',
  body: 'Final payments and who gets tipped what. The drop-off list: what goes to the venue, when, and with whom. The rain plan. The “we forgot” catch-list.',
  promise: 'The part every couple says was the worst — handled, day by day.',
  heart: true
}, {
  number: '06',
  title: 'The Day Itself',
  phase: 'Run of show, emergency kit, and the morning after',
  headline: 'The minute-by-minute run of show',
  body: 'The emergency kit inventory. The script of who directs what — so nobody has to ask you anything. And the day-after checklist (returns, payments, thank-yous) nobody remembers to plan.',
  promise: 'Be a guest at your own wedding.'
}];
const includes = ['Exactly what’s due this month — and why', 'The order things have to happen in', 'The numbers, done for you', 'Word-for-word scripts and printable templates', 'Nothing from any other phase'];
export function Volumes() {
  return <section aria-labelledby="volumes-heading" className="w-full bg-ivory" data-id="element-227" data-mp="2wcm5k6d0bwve">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-id="element-228" data-mp="2wahyjfjtahqw">
        <h2 id="volumes-heading" className="font-serif font-normal text-ink text-[28px] sm:text-[40px] text-center" data-id="element-229" data-mp="2scbuifacyg9o">
          Six volumes. Twelve months. Zero guessing.
        </h2>
        <div className="mx-auto mt-6 h-px w-32 bg-sage/40" aria-hidden="true" data-id="element-230" data-mp="2icl34mh3ghdt" />

        <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-231" data-mp="1bxk2i3h4jb0w">
          {volumes.map(v => <li key={v.number} className={`flex flex-col rounded p-6 sm:p-8 ${v.heart ? 'md:col-span-2 bg-white border border-sage/40 shadow-card' : 'bg-sand border border-sage/20'}`} data-id="element-232" data-mp="16wwbhhswau6o">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2" data-id="element-233" data-mp="2luyypi5faorq">
                <span className="font-serif font-normal text-sage-deep text-[15px]" data-id="element-234" data-mp="2qrz9259b2don">
                  Volume {v.number}
                </span>
                {v.heart && <span className="inline-flex items-center gap-1.5 rounded bg-sage-tint px-2.5 py-1 text-[12px] font-semibold uppercase tracking-kicker text-sage-deep" data-id="element-235" data-mp="3ijojbg8fpvhe">
                    <StarIcon className="w-3 h-3 fill-sage-deep" aria-hidden="true" data-id="element-236" data-mp="34m9n9snoepgg" />
                    The heart of the system
                  </span>}
              </div>

              <h3 className={`mt-2 font-serif font-medium text-ink ${v.heart ? 'text-[32px] sm:text-[38px]' : 'text-[26px]'}`} data-id="element-237" data-mp="1y54oz9gehgbp">
                {v.title}
              </h3>
              <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-kicker text-sage-deep" data-id="element-238" data-mp="1lnljn3eciczz">
                {v.phase}
              </p>

              <p className="mt-5 font-serif font-medium text-ink text-[19px] leading-snug" data-id="element-239" data-mp="1ulxifspd8h3i">
                {v.headline}
              </p>
              <p className="mt-3 text-[17px] leading-[1.6] text-ink-soft" data-id="element-240" data-mp="16wurtvcr7dwv">
                {v.body}
              </p>

              <p className="mt-auto pt-6 font-serif italic text-sage-deep text-[19px] leading-snug" data-id="element-241" data-mp="12t6ip88h4a9i">
                <span aria-hidden="true" data-id="element-242" data-mp="1go8z4pay5i0k">→ </span>
                {v.promise}
              </p>
            </li>)}
        </ul>

        <div className="mt-14 mx-auto max-w-2xl text-center" data-id="element-243" data-mp="o5bpzrugu5wf">
          <p className="text-[13px] font-semibold uppercase tracking-kicker text-sage-deep" data-id="element-244" data-mp="o3qwm5jw0t05">
            Inside every volume
          </p>
          <ul className="mt-6 inline-flex flex-col gap-3 text-left" data-id="element-245" data-mp="2ja1lcegsjtmw">
            {includes.map(item => <li key={item} className="flex gap-3" data-id="element-246" data-mp="2o7bb5v83e7b9">
                <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0 text-sage" aria-hidden="true" data-id="element-247" data-mp="2sz3246v5uf1x" />
                <span className="text-[17px] leading-[1.6] text-ink-soft" data-id="element-248" data-mp="2xwusuxk2jv4j">
                  {item}
                </span>
              </li>)}
          </ul>

          <div className="mt-10 flex flex-col items-center" data-id="element-249" data-mp="258yxpxa5eb9v">
            <CtaButton data-id="element-250" data-mp="25bl6g08rywto">I want the complete plan</CtaButton>
            <TrustLine className="mt-4 justify-center" data-id="element-251" data-mp="2a7iku10h9t3t" />
          </div>
        </div>
      </div>
    </section>;
}